import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, FileText, Loader2 } from "lucide-react";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_party-in-2hrs/artifacts/47ayyz1k_SUMIT%20DOOZYGO%20LOGO%20-%20AUG%202023.png";

const PolicyPage = ({ contentKey, icon: Icon, fallbackTitle }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await fetch(`${API_URL}/api/site-content/${contentKey}`);
        if (res.ok) {
          const data = await res.json();
          setContent(data.content);
        }
      } catch (err) {
        logger.error(`Failed to fetch ${contentKey}:`, err);
      }
      setLoading(false);
    };
    fetchContent();
  }, [contentKey]);

  const title = content?.title || fallbackTitle;
  const lastUpdated = content?.last_updated || "";
  const sections = content?.sections || [];

  return (
    <div className="min-h-screen bg-[#FFFAF5]">
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <Link to="/" className="p-2 hover:bg-[#FFFAF5] rounded-full transition-colors" data-testid="back-link">
            <ArrowLeft size={24} className="text-[#1E1B4B]" />
          </Link>
          <img src={LOGO_URL} alt="DoozyGo" className="h-10" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] rounded-xl flex items-center justify-center">
              <Icon size={24} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-[#1E1B4B] font-['Outfit']" data-testid="policy-title">{title}</h1>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={32} className="animate-spin text-[#E83E8C]" />
            </div>
          ) : (
            <div className="prose prose-lg max-w-none text-[#475569]" data-testid="policy-content">
              {lastUpdated && <p className="text-sm text-[#E83E8C] mb-6">Last updated: {lastUpdated}</p>}

              {sections.map((section, idx) => (
                <div key={section.heading || `sec-${idx}`}>
                  <h2 className="text-xl font-bold text-[#1E1B4B] mt-8 mb-4">
                    {idx + 1}. {section.heading}
                  </h2>
                  <ul className="list-disc pl-6 space-y-2">
                    {section.items.map((item, itemIdx) => (
                      <li key={`${section.heading}-${itemIdx}`}>
                        {item.split(/\*\*(.*?)\*\*/).map((part, pi) =>
                          pi % 2 === 1 ? <strong key={`b-${pi}`}>{part}</strong> : part
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              <h2 className="text-xl font-bold text-[#1E1B4B] mt-8 mb-4">Contact</h2>
              <p>For any queries:</p>
              <ul className="list-none space-y-2">
                <li>Email: doozygo@gmail.com</li>
                <li>WhatsApp: <a href="https://wa.me/919930001848" target="_blank" rel="noopener noreferrer" className="text-[#E83E8C] underline">Chat with us on WhatsApp</a></li>
              </ul>
            </div>
          )}
        </div>
      </main>

      <footer className="bg-white border-t py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-[#475569] text-sm">
          &copy; 2024 DoozyGo® - A Brand of Big Kreation Entertainment Pvt Ltd.
        </div>
      </footer>
    </div>
  );
};

export const PrivacyPolicy = () => (
  <PolicyPage contentKey="privacy_policy" icon={Shield} fallbackTitle="Privacy Policy" />
);

export const TermsOfService = () => (
  <PolicyPage contentKey="terms_of_service" icon={FileText} fallbackTitle="Terms of Service" />
);
